import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Trash2, RotateCcw, RefreshCw, Copy, Check, Download, KeyRound, Power, PowerOff, Settings2 } from 'lucide-react';
import { vpnIpsecAPI, vpnServersAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, PageHeader, Alert, useSort } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)', transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 180, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const SyncBadge = ({ status }) => {
  const map = { synced: { color: 'var(--accent-green)', bg: 'rgba(16,185,129,0.12)' }, pending: { color: 'var(--accent-orange)', bg: 'rgba(245,158,11,0.12)' }, error: { color: 'var(--accent-red)', bg: 'rgba(239,68,68,0.12)' } };
  const s = map[status] || { color: 'var(--text-muted)', bg: 'var(--bg-input)' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44` }}>{status || '—'}</span>;
};

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'inline-flex', verticalAlign: -2 }}>
      {copied ? <Check size={12} color="var(--accent-green)" /> : <Copy size={12} />}
    </button>
  );
};

const downloadFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const EMPTY_FORM = { username: '', vpn_server_id: '', password: '', static_ip: '', allowed_ips: '', status: 'active' };

export default function VpnIpsec() {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [servers, setServers] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [serverFilter, setServerFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [availableIps, setAvailableIps] = useState({ available: [], dynamic_pool: '', static_range: null });

  const [createdUser, setCreatedUser] = useState(null); // hasil create/regen/restore — sekali tampil password

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ allowed_ips: '', static_ip: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [regenConfirm, setRegenConfirm] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const { sort, onSortChange, sortParam } = useSort();

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  useEffect(() => {
    vpnServersAPI.list({ type: 'ipsec', status: 'active' }).then(r => setServers(r.vpn_servers || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedTenant) { setRows([]); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (onlyDeleted) params.only_deleted = true;
      if (serverFilter) params.vpn_server_id = serverFilter;
      if (q) params.q = q;
      if (sortParam) params.sort = sortParam;
      const res = await vpnIpsecAPI.list(selectedTenant, params);
      setRows(res.vpn_ipsec_users || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, perPage, onlyDeleted, serverFilter, q, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, onlyDeleted, serverFilter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    if (!selectedTenant) { setError('Pilih tenant terlebih dahulu.'); return; }
    if (servers.length === 0) { setError('Belum ada VPN server (ipsec) yang terdaftar & aktif.'); return; }
    setForm({ ...EMPTY_FORM, vpn_server_id: servers[0].id });
    setAvailableIps({ available: [], dynamic_pool: '', static_range: null }); setFormError(null); setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen || !form.vpn_server_id) return;
    vpnIpsecAPI.availableIps(selectedTenant, form.vpn_server_id)
      .then(r => setAvailableIps(r))
      .catch(() => setAvailableIps({ available: [], dynamic_pool: '', static_range: null }));
  }, [modalOpen, form.vpn_server_id, selectedTenant]);

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.static_ip) delete payload.static_ip;
      if (!payload.allowed_ips) delete payload.allowed_ips;
      const res = await vpnIpsecAPI.create(selectedTenant, payload);
      setModalOpen(false); setCreatedUser(res); setSuccess('User ditambahkan.'); load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await vpnIpsecAPI.deletePermanent(selectedTenant, deleteConfirm.id);
      else await vpnIpsecAPI.delete(selectedTenant, deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'User dihapus permanen.' : 'User dihapus.'); load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (u) => {
    try { const res = await vpnIpsecAPI.restore(selectedTenant, u.id); setSuccess('User dipulihkan.'); setCreatedUser(res); load(); }
    catch (e) { setError(e.message); }
  };

  const handleSync = async (u) => {
    setSyncingId(u.id);
    try { await vpnIpsecAPI.sync(selectedTenant, u.id); setSuccess('User di-sync.'); load(); }
    catch (e) { setError(e.message); } finally { setSyncingId(null); }
  };

  const handleViewConfig = async (u) => {
    try { const res = await vpnIpsecAPI.get(selectedTenant, u.id); setCreatedUser(res); }
    catch (e) { setError(e.message); }
  };

  const handleToggleStatus = async (u) => {
    try {
      await vpnIpsecAPI.update(selectedTenant, u.id, { status: u.status === 'active' ? 'inactive' : 'active' });
      setSuccess(u.status === 'active' ? 'User di-disable.' : 'User di-enable.'); load();
    } catch (e) { setError(e.message); }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ allowed_ips: u.allowed_ips || '', static_ip: u.static_ip || '' });
    setEditError(null); setRegenConfirm(false);
  };

  const handleEditSave = async () => {
    setEditSaving(true); setEditError(null);
    try {
      const payload = {};
      if (editForm.allowed_ips !== (editTarget.allowed_ips || '')) payload.allowed_ips = editForm.allowed_ips;
      if (editForm.static_ip !== (editTarget.static_ip || '')) payload.static_ip = editForm.static_ip;
      if (Object.keys(payload).length === 0) { setEditTarget(null); return; }
      const res = await vpnIpsecAPI.update(selectedTenant, editTarget.id, payload);
      setEditTarget(null); setSuccess('User diperbarui.');
      if (payload.static_ip !== undefined) setCreatedUser(res);
      load();
    } catch (e) { setEditError(e.message); }
    finally { setEditSaving(false); }
  };

  const handleRegenerate = async () => {
    setEditSaving(true); setEditError(null);
    try {
      const res = await vpnIpsecAPI.update(selectedTenant, editTarget.id, { regenerate_password: true });
      setEditTarget(null); setRegenConfirm(false);
      setSuccess('Password baru digenerate. Kasih tahu user password barunya.');
      setCreatedUser(res); load();
    } catch (e) { setEditError(e.message); }
    finally { setEditSaving(false); }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => vpnIpsecAPI.deletePermanent(selectedTenant, id)));
      else await Promise.all(selected.map(id => vpnIpsecAPI.delete(selectedTenant, id)));
      setSuccess(`${selected.length} user ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => vpnIpsecAPI.restore(selectedTenant, id)));
      setSuccess(`${selected.length} user dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try {
      await Promise.all(selected.map(id => vpnIpsecAPI.sync(selectedTenant, id)));
      setSuccess(`${selected.length} user di-sync.`); load();
    } catch (e) { setError(e.message); }
    finally { setBulkSyncing(false); }
  };

  const columns = [
    { key: 'username', label: 'Username', sortable: true, render: v => <span className="mono" style={{ fontSize: 12, color: 'var(--text-mono)' }}>{v}</span> },
    { key: 'vpn_server_name', label: 'Server', render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v || '—'}</span> },
    { key: 'static_ip', label: 'Static IP', render: v => <span className="mono" style={{ fontSize: 12 }}>{v || 'dynamic'}</span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'sync_status', label: 'Sync', sortable: true, render: v => <SyncBadge status={v} /> },
    {
      key: 'id', label: '', render: (_, u) => {
        const btnRef = React.createRef();
        return (
          <div>
            <button ref={btnRef} onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === u.id ? null : u.id); }}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>···</button>
            <DropdownPortal anchorRef={btnRef} open={openMenuId === u.id}>
              {onlyDeleted ? (
                <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(u); setOpenMenuId(null); }}><RotateCcw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Restore</MenuItem>
              ) : (<>
                <MenuItem onClick={() => { handleViewConfig(u); setOpenMenuId(null); }}><KeyRound size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Lihat Config</MenuItem>
                <MenuItem onClick={() => { openEdit(u); setOpenMenuId(null); }}><Settings2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Edit / Regenerate</MenuItem>
                <MenuItem onClick={() => { handleToggleStatus(u); setOpenMenuId(null); }}>
                  {u.status === 'active' ? <PowerOff size={13} style={{ marginRight: 6, verticalAlign: -2 }} /> : <Power size={13} style={{ marginRight: 6, verticalAlign: -2 }} />}
                  {u.status === 'active' ? 'Disable' : 'Enable'}
                </MenuItem>
                <MenuItem loading={syncingId === u.id} onClick={() => { handleSync(u); setOpenMenuId(null); }}><RefreshCw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Sync</MenuItem>
                <div style={{ borderTop: '1px solid var(--border)' }} />
                <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(u); setDeletePermanent(false); setOpenMenuId(null); }}><Trash2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Hapus</MenuItem>
              </>)}
            </DropdownPortal>
          </div>
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="VPN — IPsec Peer"
        subtitle={`${total} user${onlyDeleted ? ' (terhapus)' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <Button variant="ghost" loading={bulkSyncing} onClick={handleBulkSync}><RefreshCw size={13} /> Sync {selected.length}</Button>}
              {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {(!onlyDeleted || isSuperadmin) && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            <Button onClick={openCreate} disabled={!selectedTenant}><Plus size={14} /> Tambah User</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari username…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={serverFilter} onChange={e => setServerFilter(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Server</option>
          {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={rows} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={selectedTenant ? (onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada user L2TP/IPsec') : 'Pilih tenant terlebih dahulu'}
          selected={selected} onSelect={setSelected} />
        <div style={{ padding: '0 16px' }}><Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} /></div>
      </Card>

      {/* Create Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Tambah User L2TP/IPsec" width={460}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Username *" value={form.username} onChange={e => f('username', e.target.value)} placeholder="budi" />
          <Select label="VPN Server *" value={form.vpn_server_id} onChange={e => f('vpn_server_id', +e.target.value)}>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.pool})</option>)}
          </Select>
          <Input label="Password" type="text" value={form.password} onChange={e => f('password', e.target.value)} placeholder="kosong = auto-generate" />
          <Select label="Static IP" value={form.static_ip} onChange={e => f('static_ip', e.target.value)}>
            <option value="">— Dynamic (pool {availableIps.dynamic_pool}) —</option>
            {availableIps.available.map(ip => <option key={ip} value={ip}>{ip}</option>)}
          </Select>
          <Input label="Allowed IPs (routes client)" value={form.allowed_ips} onChange={e => f('allowed_ips', e.target.value)} placeholder="kosong = pakai default server" />
          <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>Tambah User</Button>
        </div>
      </Modal>

      {/* Edit / Regenerate Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title={`Edit User — ${editTarget?.username || ''}`} width={440}>
        {editError && <Alert type="error" message={editError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Static IP (kosongkan buat dynamic)" value={editForm.static_ip} onChange={e => setEditForm(p => ({ ...p, static_ip: e.target.value }))} />
          <Input label="Allowed IPs" value={editForm.allowed_ips} onChange={e => setEditForm(p => ({ ...p, allowed_ips: e.target.value }))} />
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Username tidak bisa diganti — hapus lalu buat user baru kalau perlu.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setEditTarget(null)}>Batal</Button>
          <Button onClick={handleEditSave} loading={editSaving}>Simpan</Button>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', marginTop: 20, paddingTop: 16 }}>
          {!regenConfirm ? (
            <Button variant="danger" size="sm" onClick={() => setRegenConfirm(true)}><KeyRound size={13} /> Regenerate Password</Button>
          ) : (
            <div>
              <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 10 }}>
                ⚠️ Password lama LANGSUNG tidak valid — kasih tahu user password barunya.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setRegenConfirm(false)}>Batal</Button>
                <Button variant="danger" size="sm" onClick={handleRegenerate} loading={editSaving}>Ya, Regenerate</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Config reveal (create / regenerate / restore / view) */}
      <Modal open={!!createdUser} onClose={() => setCreatedUser(null)} title={`Config User — ${createdUser?.username || ''}`} width={560}>
        {createdUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {createdUser.password && (
              <p style={{ fontSize: 12, color: 'var(--accent-orange)' }}>
                ⚠️ Password cuma ditampilkan sekali. Simpan/download sekarang.
              </p>
            )}
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Config (manual setting OS) <CopyBtn value={createdUser.config} /></div>
              <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{createdUser.config}</pre>
            </div>
            {createdUser.config_routeros && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Script Mikrotik RouterOS <CopyBtn value={createdUser.config_routeros} /></div>
                <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{createdUser.config_routeros}</pre>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {createdUser.config && <Button variant="secondary" onClick={() => downloadFile(`${createdUser.username}-l2tp.txt`, createdUser.config)}><Download size={13} /> Download</Button>}
              <Button onClick={() => setCreatedUser(null)}>Selesai</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus User" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus user <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.username}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ User ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk User" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} user</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ User ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus</Button>
        </div>
      </Modal>
    </div>
  );
}
